import * as THREE from 'three';
import { GeometryUtils } from '../../../utils/geometry/GeometryUtils';
import { InsideType } from '../../../constants/enum';

/**
 * InsideReferenceManager
 * Quản lý việc convert UUID references sang identifier và ngược lại
 * để đảm bảo tính nhất quán khi save/load design
 */
class InsideReferenceManager {
  constructor() {
    this.tolerance = 0.0001; // Dung sai cho so sánh position
    this.precision = 4; // Số chữ số thập phân khi làm tròn
  }

  /**
   * Tạo unique identifier từ mesh dựa vào name và WORLD position
   * Format: "name@x_y_z"
   * @param {THREE.Object3D} mesh - Mesh cần tạo identifier
   * @returns {string|null} - Identifier hoặc null nếu mesh không hợp lệ
   */
  createMeshIdentifier(mesh, referenceGroup) {
    if (!mesh) return null;

    const name = mesh.name || 'unnamed';

    // Sử dụng world position
    const worldPos = new THREE.Vector3();
    mesh.getWorldPosition(worldPos);

    const localPos = referenceGroup.worldToLocal(worldPos.clone());

    const meshPos = localPos;

    // Làm tròn position
    const x = meshPos.x.toFixed(this.precision);
    const y = meshPos.y.toFixed(this.precision);
    const z = meshPos.z.toFixed(this.precision);

    return `${name}@${x}_${y}_${z}`;
  }

  /**
   * Parse identifier thành object
   * @param {string} identifier - Format: "name@x_y_z"
   * @returns {Object|null} - {name, position: {x, y, z}} hoặc null
   */
  parseIdentifier(identifier) {
    if (!identifier || typeof identifier !== 'string') return null;

    const parts = identifier.split('@');
    if (parts.length !== 2) return null;

    const name = parts[0];
    const posParts = parts[1].split('_');
    if (posParts.length !== 3) return null;

    return {
      name,
      position: {
        x: parseFloat(posParts[0]),
        y: parseFloat(posParts[1]),
        z: parseFloat(posParts[2]),
      },
    };
  }

  /**
   * Tìm mesh trong module dựa vào identifier
   * @param {Object} clonedModule - Module chứa listInside
   * @param {string} identifier - Format: "name@x_y_z"
   * @returns {THREE.Object3D|null} - Mesh tìm được hoặc null
   */
  findMeshByIdentifier(clonedModule, identifier) {
    if (!clonedModule?.group) return null;

    const parsed = this.parseIdentifier(identifier);
    if (!parsed) return null;

    const { name: targetName, position: targetPos } = parsed;

    let foundMesh = null;

    // TRAVERSE toàn bộ group để tìm mesh
    clonedModule.group.traverse(child => {
      // Bỏ qua nếu đã tìm thấy
      if (foundMesh) return;

      // Chỉ xét mesh hoặc group có name
      if (!child.name && child.type !== 'Mesh' && child.type !== 'Group')
        return;

      const meshName = child.name || 'unnamed';

      // Check name
      if (meshName !== targetName && child.userData?.type !== InsideType.DOOR)
        return;

      // Lấy world position
      const meshWorldPos = new THREE.Vector3();
      child.getWorldPosition(meshWorldPos);

      const childLocalPos = clonedModule.group.worldToLocal(
        meshWorldPos.clone()
      );

      const meshPos = childLocalPos;

      // Check world position với tolerance
      const xMatch = Math.abs(meshPos.x - targetPos.x) < this.tolerance;
      const yMatch = Math.abs(meshPos.y - targetPos.y) < this.tolerance;
      const zMatch = Math.abs(meshPos.z - targetPos.z) < this.tolerance;

      if (xMatch && yMatch && zMatch) {
        foundMesh = child;
      }
    });

    return foundMesh;
  }

  /**
   * Convert một UUID sang identifier
   * @param {Object} clonedModule - Module chứa listInside
   * @param {string} uuid - UUID cần convert
   * @returns {string|null} - Identifier hoặc null
   */
  convertUuidToIdentifier(clonedModule, uuid) {
    if (!uuid || !clonedModule?.listInside) return null;

    const mesh = GeometryUtils.findMeshByUuid(clonedModule?.group, uuid);

    return mesh ? this.createMeshIdentifier(mesh, clonedModule.group) : null;
  }

  /**
   * Convert một array UUIDs sang array identifiers
   * @param {Object} clonedModule - Module chứa listInside
   * @param {Array<string>} uuids - Array UUIDs cần convert
   * @returns {Array<string>} - Array identifiers (bỏ qua null)
   */
  convertUuidsToIdentifiers(clonedModule, uuids) {
    if (!Array.isArray(uuids)) return [];

    return uuids
      .map(uuid => this.convertUuidToIdentifier(clonedModule, uuid))
      .filter(Boolean);
  }

  /**
   * Convert userData của một mesh: UUID -> Identifier
   * @param {Object} clonedModule - Module chứa listInside
   * @param {THREE.Object3D} mesh - Mesh cần convert userData
   * @returns {Object} - UserData đã được convert
   */
  convertMeshUserDataToIdentifiers(clonedModule, mesh) {
    if (!mesh?.userData) return {};

    const userData = mesh.userData;
    const converted = {};

    // Single edge references
    const singleEdgeFields = [
      'leftEdgeUuid',
      'leftEdgeDoorUuid',
      'rightEdgeUuid',
      'rightEdgeDoorUuid',
      'topEdgeUuid',
      'bottomEdgeUuid',
      'drawerUuid',
      'handleUuid',
      'lockUuid',
      'liftSupportUuid',
      'braceUuid',
      'frameUuid',
      'linkedDoorUuid',
      'mirrorUuid',
      'parentUDrawerUuid',
    ];

    singleEdgeFields.forEach(field => {
      if (userData[field]) {
        const identifierField = field.replace('Uuid', 'Identifier');
        converted[identifierField] = this.convertUuidToIdentifier(
          clonedModule,
          userData[field]
        );
      }
    });

    // Dependent arrays
    const dependentFields = [
      'rightDependent',
      'leftDependent',
      'topDependent',
      'bottomDependent',
    ];

    dependentFields.forEach(field => {
      if (userData[field] && Array.isArray(userData[field])) {
        const identifierField = field + 'Identifiers';
        converted[identifierField] = this.convertUuidsToIdentifiers(
          clonedModule,
          userData[field]
        );
      }
    });

    // Special cases
    const specialFields = {
      doorChild: 'doorChildIdentifier',
      childUuid: 'childIdentifier',
      parentGltf: 'parentGltfIdentifier',
    };

    Object.entries(specialFields).forEach(([uuidField, identifierField]) => {
      if (userData[uuidField]) {
        converted[identifierField] = this.convertUuidToIdentifier(
          clonedModule,
          userData[uuidField]
        );
      }
    });

    return converted;
  }

  /**
   * Convert tất cả UUID references trong module thành identifiers
   * @param {Object} clonedModule - Module cần convert
   */
  convertAllUuidsToIdentifiers(clonedModule) {
    if (!clonedModule?.listInside) return;

    clonedModule.listInside.forEach((inside, index) => {
      if (!inside?.gltf?.userData) return;

      const converted = this.convertMeshUserDataToIdentifiers(
        clonedModule,
        inside.gltf
      );

      // Merge converted fields vào userData
      Object.assign(inside.gltf.userData, converted);

      // Log chi tiết
      const convertedKeys = Object.keys(converted);
      if (convertedKeys.length > 0) {
      }
    });
  }

  /**
   * Convert userData của một mesh: Identifier -> UUID
   * Tự động rebuild dependencies
   * @param {Object} clonedModule - Module chứa listInside
   * @param {THREE.Object3D} mesh - Mesh cần convert userData
   * @param {number} meshIndex - Index của mesh trong listInside (dùng cho log)
   */
  convertMeshUserDataToUuids(clonedModule, mesh, meshIndex = 0) {
    if (!mesh?.userData) return;

    const userData = mesh.userData;

    // Single edge references with auto-rebuild dependencies
    const edgeConfigs = [
      {
        identifierField: 'leftEdgeIdentifier',
        uuidField: 'leftEdgeUuid',
        addToDependent: edgeMesh => {
          if (!edgeMesh.userData.rightDependent) {
            edgeMesh.userData.rightDependent = [];
          }
          if (!edgeMesh.userData.rightDependent.includes(mesh.uuid)) {
            edgeMesh.userData.rightDependent.push(mesh.uuid);
          }
        },
      },
      {
        identifierField: 'leftEdgeDoorIdentifier',
        uuidField: 'leftEdgeDoorUuid',
        addToDependent: edgeMesh => {},
      },
      {
        identifierField: 'rightEdgeDoorIdentifier',
        uuidField: 'rightEdgeDoorUuid',
        addToDependent: edgeMesh => {},
      },
      {
        identifierField: 'rightEdgeIdentifier',
        uuidField: 'rightEdgeUuid',
        addToDependent: edgeMesh => {
          if (!edgeMesh.userData.leftDependent) {
            edgeMesh.userData.leftDependent = [];
          }
          if (!edgeMesh.userData.leftDependent.includes(mesh.uuid)) {
            edgeMesh.userData.leftDependent.push(mesh.uuid);
          }
        },
      },
      {
        identifierField: 'topEdgeIdentifier',
        uuidField: 'topEdgeUuid',
        addToDependent: edgeMesh => {
          if (!edgeMesh.userData.bottomDependent) {
            edgeMesh.userData.bottomDependent = [];
          }
          if (!edgeMesh.userData.bottomDependent.includes(mesh.uuid)) {
            edgeMesh.userData.bottomDependent.push(mesh.uuid);
          }
        },
      },
      {
        identifierField: 'bottomEdgeIdentifier',
        uuidField: 'bottomEdgeUuid',
        addToDependent: edgeMesh => {
          if (!edgeMesh.userData.topDependent) {
            edgeMesh.userData.topDependent = [];
          }
          if (!edgeMesh.userData.topDependent.includes(mesh.uuid)) {
            edgeMesh.userData.topDependent.push(mesh.uuid);
          }
        },
      },
      {
        identifierField: 'drawerIdentifier',
        uuidField: 'drawerUuid',
        addToDependent: edgeMesh => {},
      },
      {
        identifierField: 'handleIdentifier',
        uuidField: 'handleUuid',
        addToDependent: edgeMesh => {},
      },
      {
        identifierField: 'lockIdentifier',
        uuidField: 'lockUuid',
        addToDependent: edgeMesh => {},
      },
      {
        identifierField: 'liftSupportIdentifier',
        uuidField: 'liftSupportUuid',
        addToDependent: edgeMesh => {},
      },
      {
        identifierField: 'braceIdentifier',
        uuidField: 'braceUuid',
        addToDependent: edgeMesh => {},
      },
      {
        identifierField: 'frameIdentifier',
        uuidField: 'frameUuid',
        addToDependent: edgeMesh => {},
      },
      {
        identifierField: 'linkedDoorIdentifier',
        uuidField: 'linkedDoorUuid',
        addToDependent: edgeMesh => {},
      },
      {
        identifierField: 'mirrorIdentifier',
        uuidField: 'mirrorUuid',
        addToDependent: edgeMesh => {},
      },
      {
        identifierField: 'parentUDrawerIdentifier',
        uuidField: 'parentUDrawerUuid',
        addToDependent: edgeMesh => {},
      },
      {
        identifierField: 'parentUDrawerIdentifier',
        uuidField: 'parentUDrawerUuid',
        addToDependent: edgeMesh => {},
      },
    ];

    edgeConfigs.forEach(config => {
      if (userData[config.identifierField]) {
        const edgeMesh = this.findMeshByIdentifier(
          clonedModule,
          userData[config.identifierField]
        );

        if (edgeMesh) {
          userData[config.uuidField] = edgeMesh.uuid;
          config.addToDependent(edgeMesh);
        } else {
          console.warn(
            `  ⚠️ [${meshIndex}] Cannot find edge mesh: ${
              userData[config.identifierField]
            }`
          );
        }
      }
    });

    // Dependent arrays
    const dependentConfigs = [
      {
        identifierField: 'rightDependentIdentifiers',
        uuidField: 'rightDependent',
      },
      {
        identifierField: 'leftDependentIdentifiers',
        uuidField: 'leftDependent',
      },
      { identifierField: 'topDependentIdentifiers', uuidField: 'topDependent' },
      {
        identifierField: 'bottomDependentIdentifiers',
        uuidField: 'bottomDependent',
      },
    ];

    dependentConfigs.forEach(config => {
      if (
        userData[config.identifierField] &&
        Array.isArray(userData[config.identifierField])
      ) {
        userData[config.uuidField] = userData[config.identifierField]
          .map(identifier => {
            const foundMesh = this.findMeshByIdentifier(
              clonedModule,
              identifier
            );
            return foundMesh ? foundMesh.uuid : null;
          })
          .filter(Boolean);
      }
    });

    // Special cases
    const specialConfigs = [
      { identifierField: 'doorChildIdentifier', uuidField: 'doorChild' },
      { identifierField: 'childIdentifier', uuidField: 'childUuid' },
      { identifierField: 'parentGltfIdentifier', uuidField: 'parentGltf' },
    ];

    specialConfigs.forEach(config => {
      if (userData[config.identifierField]) {
        const foundMesh = this.findMeshByIdentifier(
          clonedModule,
          userData[config.identifierField]
        );

        if (foundMesh) {
          userData[config.uuidField] = foundMesh.uuid;
        }
      }
    });
  }

  /**
   * Convert tất cả identifier references trong module thành UUIDs
   * @param {Object} clonedModule - Module cần convert
   */
  convertAllIdentifiersToUuids(clonedModule) {
    if (!clonedModule?.listInside) return;

    clonedModule.listInside.forEach((inside, index) => {
      if (!inside?.gltf?.userData) return;

      this.convertMeshUserDataToUuids(clonedModule, inside.gltf, index);
    });
  }

  /**
   * Clean userData trước khi save - bỏ các UUID fields
   * @param {Object} userData - UserData cần clean
   * @returns {Object} - UserData đã được clean (copy mới)
   */
  cleanUserDataForSave(userData) {
    if (!userData) return {};

    const cleaned = { ...userData };

    // Danh sách các fields cần bỏ (chứa UUID)
    const fieldsToRemove = [
      'leftEdgeUuid',
      'rightEdgeUuid',
      'topEdgeUuid',
      'bottomEdgeUuid',
      'rightDependent',
      'leftDependent',
      'topDependent',
      'bottomDependent',
      'doorChild',
      'childUuid',
      'parentGltf',
    ];

    fieldsToRemove.forEach(field => {
      delete cleaned[field];
    });

    return cleaned;
  }

  /**
   * Validate identifier format
   * @param {string} identifier - Identifier cần validate
   * @returns {boolean} - true nếu valid
   */
  isValidIdentifier(identifier) {
    if (!identifier || typeof identifier !== 'string') return false;

    const parsed = this.parseIdentifier(identifier);
    if (!parsed) return false;

    const { position } = parsed;
    return !isNaN(position.x) && !isNaN(position.y) && !isNaN(position.z);
  }

  /**
   * Debug: In ra thống kê về references trong module
   * @param {Object} clonedModule - Module cần debug
   */
  debugModuleReferences(clonedModule) {
    if (!clonedModule?.listInside) {
      console.log(' No listInside found');
      return;
    }

    console.log('📊 Module References Debug Info:');
    console.log(`   Total inside items: ${clonedModule.listInside.length}`);

    let totalEdgeRefs = 0;
    let totalDependents = 0;
    let totalSpecialRefs = 0;

    clonedModule.listInside.forEach((inside, index) => {
      if (!inside?.gltf?.userData) return;

      const userData = inside.gltf.userData;
      const refs = [];

      // Count edge references
      ['leftEdge', 'rightEdge', 'topEdge', 'bottomEdge'].forEach(edge => {
        if (userData[edge + 'Uuid'] || userData[edge + 'Identifier']) {
          refs.push(edge);
          totalEdgeRefs++;
        }
      });

      // Count dependents
      ['right', 'left', 'top', 'bottom'].forEach(dir => {
        const depField = dir + 'Dependent';
        const depIdField = depField + 'Identifiers';
        if (userData[depField] || userData[depIdField]) {
          const count =
            (userData[depField]?.length || 0) +
            (userData[depIdField]?.length || 0);
          if (count > 0) {
            refs.push(`${dir}Dep(${count})`);
            totalDependents += count;
          }
        }
      });

      // Count special refs
      if (userData.doorChild || userData.doorChildIdentifier) {
        refs.push('doorChild');
        totalSpecialRefs++;
      }
      if (userData.childUuid || userData.childIdentifier) {
        refs.push('holder');
        totalSpecialRefs++;
      }
      if (userData.parentGltf || userData.parentGltfIdentifier) {
        refs.push('parent');
        totalSpecialRefs++;
      }

      if (refs.length > 0) {
        console.log(`   [${index}] ${inside.gltf.name}: ${refs.join(', ')}`);
      }
    });

    console.log(`   Total edge references: ${totalEdgeRefs}`);
    console.log(`   Total dependents: ${totalDependents}`);
    console.log(`   Total special references: ${totalSpecialRefs}`);
  }
}

export default InsideReferenceManager;
